import HOC from "../../components/HOC";

import * as emoji from "node-emoji";
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import {
  Snackbar,
  IconButton,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import Logo from "../../assets/olinfull.png";
import OlinMascot from "../../assets/olinCharaLogin.png";
import { useNavigate } from "react-router-dom";
import { BsCheckCircle } from "react-icons/bs";
import * as yup from "yup";
import { register } from "../../services/authApi";
import ModalPopup from "../../components/common/ModalPopup";
import { IoArrowBack, IoInformationCircleOutline } from "react-icons/io5";
import { RxEyeClosed } from "react-icons/rx";
import { VscEye } from "react-icons/vsc";
import FullsizeButton from "../../components/common/FullsizeButton";
import AuthButton from "../../components/common/AuthButton";
import DatePickerInput from "../../components/common/DatePickerInput";

const textFieldStyles = {
  backgroundColor: "white",
  borderRadius: "10px",
  color: "#111827",
  transition: "all 0.4s",
  fontFamily: '"Montserrat", sans-serif',
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#d1d5db",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9ca3af",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiInputBase-input": {
    padding: "14px 12px",
    color: "#111827",
    fontSize: { xs: "14px", lg: "16px" },
    fontFamily: '"Montserrat", sans-serif',
    "&::placeholder": {
      color: "#CBCBCB",
      opacity: 1,
      fontFamily: '"Montserrat", sans-serif',
    },
  },
  "&.Mui-disabled": {
    backgroundColor: "#f9fafb",
    opacity: 0.7,
  },
};

const errorTextFieldStyles = {
  ...textFieldStyles,
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#ef4444",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ef4444",
  },
};

const formHelperFieldStyles = {
  width: "100%",
  display: "flex",
  justifyContent: "start",
  margin: "2px 0 0 0",
  fontSize: "12px",
  color: "#ef4444",
  fontFamily: '"Montserrat", sans-serif',
};

const getCleanPhoneNumber = (formatted) => {
  return formatted.replace(/[^\d+]/g, "");
};

const validationSchema = yup.object({
  fullname: yup.string().required("Nama lengkap wajib diisi"),
  email: yup
    .string()
    .email("Format email tidak valid")
    .required("Email wajib diisi"),
  nik: yup
    .string()
    .required("NIK wajib diisi")
    .matches(/^\d{16}$/, "NIK harus terdiri dari 16 digit angka"),
  phone_number: yup
    .string()
    .required("Nomor ponsel wajib diisi")
    .test(
      "phone-validation",
      "Format nomor ponsel tidak valid (min. 10 digit)",
      function (value) {
        if (!value) return false;
        const cleanPhone = getCleanPhoneNumber(value);
        const phoneRegex = /^\+628\d{9,13}$/;
        return phoneRegex.test(cleanPhone);
      },
    ),
  date_of_birth: yup.string().required("Tanggal lahir wajib diisi"),
  gender: yup
    .number()
    .min(1, "Jenis kelamin wajib dipilih")
    .required("Jenis kelamin wajib dipilih"),
  password: yup
    .string()
    .min(8, "Password minimal 8 karakter")
    .required("Password wajib diisi"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Password tidak sama")
    .required("Konfirmasi password wajib diisi"),
  agreeToTerms: yup
    .boolean()
    .oneOf([true], "Anda harus menyetujui Syarat & Ketentuan")
    .required("Anda harus menyetujui Syarat & Ketentuan"),
});

const SignUp = () => {
  const navigate = useNavigate();

  const [statusCode, setStatusCode] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [open, setOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const getFieldStyles = (hasError) => {
    return hasError ? errorTextFieldStyles : textFieldStyles;
  };

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;
    setStatusCode(false);
    setErrorMessage("");
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/[^\d+]/g, "");

    if (cleaned.startsWith("+62")) {
      const numbers = cleaned.substring(3);
      const limitedNumbers = numbers.substring(0, 13);

      return "(+62) " + limitedNumbers;
    }

    return value;
  };

  const handlePhoneChange = (value) => {
    let cleaned = value.replace(/[^\d]/g, "");

    if (cleaned.startsWith("0")) {
      cleaned = "62" + cleaned.substring(1);
    } else if (cleaned.startsWith("8")) {
      cleaned = "62" + cleaned;
    } else if (!cleaned.startsWith("62")) {
      cleaned = "62" + cleaned;
    }

    cleaned = cleaned.substring(0, 15);

    const formatted = formatPhoneNumber("+" + cleaned);
    formik.setFieldValue("phone_number", formatted);
    setErrorMessage("");
  };

  const formik = useFormik({
    initialValues: {
      fullname: "",
      email: "",
      nik: "",
      phone_number: "",
      date_of_birth: "",
      gender: 0,
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
    validationSchema,
    onSubmit: (value) => {
      const submitData = {
        fullname: value.fullname,
        email: value.email.toLowerCase(),
        nik: value.nik,
        phone_number: getCleanPhoneNumber(value.phone_number),
        date_of_birth: value.date_of_birth,
        gender: value.gender,
        password: value.password,
      };
      mutateRegister(submitData);
    },
    validateOnMount: true,
  });

  const {
    mutate: mutateRegister,
    data,
    isPending,
  } = useMutation({
    mutationFn: register,
    onSuccess: () => {
      setOpen(true);
    },
    onError: (err) => {
      setErrorMessage(err.message.id || "Terjadi kesalahan saat mendaftar");
    },
  });

  return (
    <div className="bg-white relative">
      <FullsizeButton />
      <div className="flex flex-row justify-center min-h-screen">
        <div className="hidden md:order-last bg-cover lg:block lg:w-1/2">
          <div className="flex items-center h-full p-8 bg-white justify-center">
            <div className="w-[30vw] h-[30vw] flex justify-center object-cover items-center rounded-full p-8 bg-blue-100">
              <div className="p-16 bg-blue-300 rounded-full w-full h-full">
                <img
                  src={OlinMascot}
                  className="w-full h-full"
                  alt="Olin Maskot"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/2 flex justify-center lg:items-center px-0 lg:px-12 text-base">
          <div className="rounded-md p-4 lg:p-8 w-full h-fit">
            <div className="w-full flex items-center gap-4 lg:hidden text-xl">
              <div
                onClick={() => navigate(-1)}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <IoArrowBack className="text-gray-500" />
              </div>
              <p className="font-semibold text-primary-700">Daftar</p>
            </div>
            <img
              src={Logo}
              alt="Logo OLIN"
              className={`my-8 ${
                isMobileView ? "w-[80px] mt-12" : "w-[100px] mt-16"
              }`}
            />
            <h2 className="text-3xl font-bold text-black my-10 lg:my-4 items-center hidden lg:flex gap-2">
              <p>Selamat Datang di OLIN</p>
              {emoji.emojify(":wave:")}
            </h2>

            <p className="mb-8 text-lg text-disableText hidden lg:block">
              Daftarkan akun Anda dan mulai jelajahi berbagai fitur kami!
            </p>

            <form onSubmit={formik.handleSubmit}>
              <div className="space-y-4">
                <div>
                  <p>Nama Lengkap</p>
                  <TextField
                    fullWidth
                    placeholder="Masukkan nama lengkap"
                    value={formik.values.fullname}
                    onChange={(e) =>
                      formik.setFieldValue("fullname", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    name="fullname"
                    helperText={
                      formik.touched.fullname && formik.errors.fullname
                    }
                    error={
                      formik.touched.fullname && Boolean(formik.errors.fullname)
                    }
                    InputProps={{
                      sx: getFieldStyles(
                        formik.errors.fullname && formik.touched.fullname,
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: formHelperFieldStyles,
                    }}
                  />
                </div>

                <div>
                  <p>Email</p>
                  <TextField
                    fullWidth
                    placeholder="Masukkan email"
                    value={formik.values.email}
                    onChange={(e) =>
                      formik.setFieldValue("email", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    name="email"
                    helperText={formik.touched.email && formik.errors.email}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    InputProps={{
                      sx: getFieldStyles(
                        formik.errors.email && formik.touched.email,
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: formHelperFieldStyles,
                    }}
                  />
                </div>

                <div>
                  <p>NIK</p>
                  <TextField
                    fullWidth
                    placeholder="Masukkan 16 digit NIK"
                    value={formik.values.nik}
                    onChange={(e) => {
                      const val = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 16);
                      formik.setFieldValue("nik", val);
                    }}
                    onBlur={formik.handleBlur}
                    name="nik"
                    inputProps={{ inputMode: "numeric", maxLength: 16 }}
                    helperText={
                      formik.touched.nik && formik.errors.nik
                        ? formik.errors.nik
                        : formik.values.nik.length > 0
                          ? `${formik.values.nik.length}/16 digit`
                          : ""
                    }
                    error={formik.touched.nik && Boolean(formik.errors.nik)}
                    InputProps={{
                      sx: getFieldStyles(
                        formik.errors.nik && formik.touched.nik,
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: {
                        ...formHelperFieldStyles,
                        color:
                          formik.touched.nik && formik.errors.nik
                            ? "#ef4444"
                            : "#9ca3af",
                      },
                    }}
                  />
                </div>

                <div>
                  <p>Nomor Ponsel</p>
                  <TextField
                    fullWidth
                    placeholder="(+62) 8xxxxxxxxxx"
                    value={formik.values.phone_number}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    onBlur={formik.handleBlur}
                    name="phone_number"
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    helperText={
                      formik.touched.phone_number && formik.errors.phone_number
                    }
                    error={
                      formik.touched.phone_number &&
                      Boolean(formik.errors.phone_number)
                    }
                    InputProps={{
                      sx: getFieldStyles(
                        formik.errors.phone_number &&
                          formik.touched.phone_number,
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: formHelperFieldStyles,
                    }}
                  />
                </div>

                <div>
                  <p>Tanggal Lahir</p>
                  <DatePickerInput
                    value={formik.values.date_of_birth}
                    onChange={(date) => {
                      formik.setFieldValue("date_of_birth", date);
                    }}
                    onBlur={() => formik.setFieldTouched("date_of_birth", true)}
                    error={
                      formik.touched.date_of_birth &&
                      Boolean(formik.errors.date_of_birth)
                    }
                    helperText={
                      formik.touched.date_of_birth &&
                      formik.errors.date_of_birth
                    }
                    placeholder="DD/MM/YYYY"
                  />
                </div>

                <div>
                  <p>Jenis Kelamin</p>
                  <FormControl
                    error={
                      formik.touched.gender && Boolean(formik.errors.gender)
                    }
                  >
                    <RadioGroup
                      row
                      value={formik.values.gender}
                      onChange={(e) =>
                        formik.setFieldValue("gender", Number(e.target.value))
                      }
                      onBlur={formik.handleBlur}
                      name="gender"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontFamily: '"Montserrat", sans-serif',
                          fontSize: "14px",
                        },
                      }}
                    >
                      <FormControlLabel
                        value={1}
                        control={<Radio />}
                        label="Laki-laki"
                      />
                      <FormControlLabel
                        value={2}
                        control={<Radio />}
                        label="Perempuan"
                      />
                    </RadioGroup>
                    {formik.touched.gender && formik.errors.gender && (
                      <FormHelperText sx={formHelperFieldStyles}>
                        {formik.errors.gender}
                      </FormHelperText>
                    )}
                  </FormControl>
                </div>

                <div>
                  <p>Password</p>
                  <TextField
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Masukkan password (min. 8 karakter)"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.password && Boolean(formik.errors.password)
                    }
                    helperText={
                      formik.touched.password && formik.errors.password
                        ? formik.errors.password
                        : ""
                    }
                    disabled={isPending}
                    InputProps={{
                      sx: getFieldStyles(
                        formik.errors.password && formik.touched.password,
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {showPassword ? (
                            <RxEyeClosed
                              onClick={() => setShowPassword(!showPassword)}
                              className="cursor-pointer"
                            />
                          ) : (
                            <VscEye
                              onClick={() => setShowPassword(!showPassword)}
                              className="cursor-pointer"
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: formHelperFieldStyles,
                    }}
                  />
                </div>

                <div>
                  <p>Konfirmasi Password</p>
                  <TextField
                    fullWidth
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Masukkan ulang password"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.confirmPassword &&
                      Boolean(formik.errors.confirmPassword)
                    }
                    helperText={
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                        ? formik.errors.confirmPassword
                        : ""
                    }
                    disabled={isPending}
                    InputProps={{
                      sx: getFieldStyles(
                        formik.errors.confirmPassword &&
                          formik.touched.confirmPassword,
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {showConfirmPassword ? (
                            <RxEyeClosed
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="cursor-pointer"
                            />
                          ) : (
                            <VscEye
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="cursor-pointer"
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    FormHelperTextProps={{
                      sx: formHelperFieldStyles,
                    }}
                  />
                </div>

                <div>
                  <FormControl
                    error={
                      formik.touched.agreeToTerms &&
                      Boolean(formik.errors.agreeToTerms)
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.agreeToTerms}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "agreeToTerms",
                              e.target.checked,
                            )
                          }
                          onBlur={formik.handleBlur}
                          name="agreeToTerms"
                        />
                      }
                      label={
                        <span
                          style={{
                            fontFamily: '"Montserrat", sans-serif',
                            fontSize: "14px",
                          }}
                        >
                          Saya menyetujui{" "}
                          <span className="text-blue-500 cursor-pointer hover:underline">
                            Syarat & Ketentuan
                          </span>{" "}
                          serta{" "}
                          <span className="text-blue-500 cursor-pointer hover:underline">
                            Kebijakan Privasi
                          </span>
                          .
                        </span>
                      }
                      sx={{
                        alignItems: "flex-start",
                        "& .MuiCheckbox-root": {
                          paddingTop: "2px",
                        },
                      }}
                    />
                    {formik.touched.agreeToTerms &&
                      formik.errors.agreeToTerms && (
                        <FormHelperText sx={formHelperFieldStyles}>
                          {formik.errors.agreeToTerms}
                        </FormHelperText>
                      )}
                  </FormControl>
                </div>

                {errorMessage && (
                  <div className="w-full flex justify-end mt-2">
                    <div className="flex items-center gap-2 bg-red-100 py-1.5 px-2.5 rounded-md">
                      <IoInformationCircleOutline
                        className="text-error shrink-0"
                        size={12}
                      />
                      <p className="text-sm text-red-700">{errorMessage}</p>
                    </div>
                  </div>
                )}

                <AuthButton
                  title={"Daftar"}
                  isPending={isPending}
                  formik={formik}
                />
              </div>
            </form>
            <hr className="my-6 lg:my-10" />
            <p className="text-center mb-8">
              Sudah punya akun?{" "}
              <span
                className="cursor-pointer text-primary hover:text-blue-700 font-medium hover:underline"
                onClick={() => navigate("/login")}
              >
                Masuk
              </span>
            </p>
          </div>
        </div>
      </div>
      <Snackbar
        open={statusCode}
        autoHideDuration={5000}
        onClose={() => {
          setStatusCode(false);
          setErrorMessage("");
        }}
        message={errorMessage}
        action={
          <>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />

      <ModalPopup
        open={open}
        setOpen={setOpen}
        path="/login"
        title="Berhasil Membuat Akun"
        // subtitle="Akun Anda telah berhasil dibuat. Silakan lakukan verifikasi melalui kode OTP yang telah dikirim ke email atau nomor ponsel Anda."
        subtitle="Akun Anda telah berhasil dibuat. Silakan login menggunakan email dan password Anda."
        icon={
          <>
            <BsCheckCircle className="text-5xl text-green-500" />
          </>
        }
      />
    </div>
  );
};
export default HOC(SignUp, "guest-only");
